import React, {useEffect, useRef} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

type Display = 'block' | 'flex' | 'inline-block' | 'inline' | 'contents'

const Wrapper = styled.div`
  display: block;

  &.--block {
    display: block;
  }
  &.--flex {
    display: flex;
  }
  &.--inline-block {
    display: inline-block;
  }
  &.--inline {
    display: inline;
  }
  &.--contents {
    display: contents;
  }
`

interface Props {
  /** Content to render inside the click boundary */
  children: React.ReactNode
  /** Callback fired when a pointer interaction is detected outside the component */
  onOutsideClick: (event: MouseEvent | TouchEvent) => void
  /** When true, outside click detection is suspended without unmounting children */
  disabled?: boolean
  /** When true, listeners are registered in the capture phase instead of the bubble phase */
  useCapture?: boolean
  /** CSS display value applied to the wrapper element */
  display?: Display
}

/** Detects clicks and taps outside of its children and fires `onOutsideClick`. */
export const OutsideClickHandler = ({
  children,
  onOutsideClick,
  disabled = false,
  useCapture = true,
  display = 'block'
}: Props) => {
  const ref = useRef<HTMLDivElement>(null)

  // Keep a ref to onOutsideClick so the effect below never needs to re-run
  // just because the callback identity changed between renders.
  const onOutsideClickRef = useRef(onOutsideClick)
  useEffect(() => {
    onOutsideClickRef.current = onOutsideClick
  })

  useEffect(() => {
    if (disabled) return

    // AbortController lets us cancel all listeners in one call on cleanup.
    const controller = new AbortController()
    const {signal} = controller

    // Shared flag prevents stacking multiple end-listeners when rapid
    // start events fire before the previous end event resolves.
    let endPending = false

    // --- Mouse ---

    const onMouseUp = (mouseUpEvent: MouseEvent) => {
      endPending = false
      const endedInside = ref.current?.contains(mouseUpEvent.target as Node)
      if (!endedInside) onOutsideClickRef.current(mouseUpEvent)
    }

    const onMouseDown = (mouseDownEvent: MouseEvent) => {
      if (endPending) return
      const startedInside = ref.current?.contains(mouseDownEvent.target as Node)
      if (startedInside) return

      endPending = true
      document.addEventListener('mouseup', onMouseUp, {
        capture: useCapture,
        once: true,
        signal
      })
    }

    // --- Touch ---

    const onTouchEnd = (touchEndEvent: TouchEvent) => {
      endPending = false
      const touch = touchEndEvent.changedTouches[0]
      const target = document.elementFromPoint(touch.clientX, touch.clientY)
      const endedInside = ref.current?.contains(target)
      if (!endedInside) onOutsideClickRef.current(touchEndEvent)
    }

    const onTouchStart = (touchStartEvent: TouchEvent) => {
      if (endPending) return
      const startedInside = ref.current?.contains(
        touchStartEvent.target as Node
      )
      if (startedInside) return

      endPending = true
      document.addEventListener('touchend', onTouchEnd, {
        capture: useCapture,
        once: true,
        signal
      })
    }

    document.addEventListener('mousedown', onMouseDown, {
      capture: useCapture,
      signal
    })
    document.addEventListener('touchstart', onTouchStart, {
      capture: useCapture,
      signal
    })

    return () => controller.abort()
  }, [disabled, useCapture])

  return (
    <Wrapper ref={ref} className={cx(display && `--${display}`)}>
      {children}
    </Wrapper>
  )
}
